import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Spinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { formatSpeed } from '../../lib/leads/leadList';
import { KC, accountKyckConnectHref, fixHref } from '../../lib/kyckConnect/links';
import { ageInWords, brandOptions, filterRows, sortRows, type OverviewSortKey, type SortDir } from '../../lib/kyckConnect/overviewView';
import type { ServiceBadgeId } from '../../lib/kyckConnect/services';
import { SETUP_STEP_LABELS } from '../../lib/kyckConnect/setupStatus';
import type { OverviewRow } from '../../types/kyckConnectOverview';
import { useCurrentUser } from '../auth';
import { fetchOverview } from './overviewApi';
import { KyckConnectHome } from './KyckConnectHome';

// THE KYCKCONNECT OVERVIEW (redesign Phase 1): what needs Kyck today, how every client is going, and what is not set up.
//
// ADMIN ONLY DATA: the endpoint spans every client's figures, so the server refuses anyone else; a non-admin sees the pages
// they can use instead of a failed request. Nothing here writes. The look is the staff app's own (Card-free tiles, plain table).

const selectClass = 'rounded-md border border-slate-300 bg-white p-1.5 text-sm';

function Tile({ to, label, value, note, testId }: { to: string; label: string; value: string | number; note: string; testId: string }) {
  return (
    <Link to={to} className="block rounded-md border border-slate-200 bg-white p-4 hover:border-slate-400" data-testid={testId}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{note}</p>
    </Link>
  );
}

const SERVICE_OPTIONS: Array<{ value: ServiceBadgeId; label: string }> = [
  { value: 'leadRelay', label: 'Lead relay' },
  { value: 'aiSms', label: 'AI SMS' },
  { value: 'speedToLead', label: 'Speed to Lead' },
];

function Header({ label, k, sort, dir, onSort, right }: { label: string; k: OverviewSortKey; sort: OverviewSortKey; dir: SortDir; onSort: (k: OverviewSortKey) => void; right?: boolean }) {
  return (
    <th className={`pb-2 font-medium ${right ? 'text-right' : 'text-left'}`} aria-sort={sort === k ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="hover:text-slate-900" onClick={() => onSort(k)}>
        {label}
        {sort === k ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
}

function healthBadge(r: OverviewRow) {
  return <Badge variant={r.health.kind === 'incomplete' ? 'warning' : r.health.kind === 'waiting' ? 'info' : 'success'}>{r.healthLabel}</Badge>;
}

function ClientTable({ rows }: { rows: OverviewRow[] }) {
  const [brandId, setBrandId] = useState<string | null>(null);
  const [service, setService] = useState<ServiceBadgeId | null>(null);
  const [sort, setSort] = useState<OverviewSortKey>('status');
  const [dir, setDir] = useState<SortDir>('asc');
  const onSort = (k: OverviewSortKey) => {
    if (k === sort) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(k);
      setDir(k === 'client' || k === 'status' ? 'asc' : 'desc');
    }
  };
  const shown = useMemo(() => sortRows(filterRows(rows, { brandId, service }), sort, dir), [rows, brandId, service, sort, dir]);
  const brands = useMemo(() => brandOptions(rows), [rows]);

  return (
    <section className="rounded-md border border-slate-200 bg-white p-4" aria-label="Client health">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Clients</h2>
        <div className="flex flex-wrap gap-2">
          <select className={selectClass} aria-label="Brand" value={brandId ?? ''} onChange={(e) => setBrandId(e.target.value === '' ? null : e.target.value)}>
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          <select className={selectClass} aria-label="Service" value={service ?? ''} onChange={(e) => setService(e.target.value === '' ? null : (e.target.value as ServiceBadgeId))}>
            <option value="">All services</option>
            {SERVICE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      {shown.length === 0 ? (
        <p className="text-sm text-slate-500">No client matches. A client appears here once it uses a KyckConnect service or has one half set up.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="client-health">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                <Header label="Client" k="client" sort={sort} dir={dir} onSort={onSort} />
                <th className="pb-2 text-left font-medium">Services</th>
                <Header label="Leads (7 days)" k="leads" sort={sort} dir={dir} onSort={onSort} right />
                <Header label="Contacted" k="contacted" sort={sort} dir={dir} onSort={onSort} right />
                <Header label="Median speed" k="speed" sort={sort} dir={dir} onSort={onSort} right />
                <Header label="Booked or joined" k="booked" sort={sort} dir={dir} onSort={onSort} right />
                <Header label="Needs a person" k="needsPerson" sort={sort} dir={dir} onSort={onSort} right />
                <Header label="Status" k="status" sort={sort} dir={dir} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.accountId} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-3 font-medium text-slate-900">
                    <Link className="underline-offset-2 hover:underline" to={accountKyckConnectHref(r.accountId)}>{r.accountName}</Link>
                    {r.brandName && <span className="block text-xs font-normal text-slate-500">{r.brandName}</span>}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">{r.badges.map((b) => <Badge key={b.id} variant="neutral">{b.label}</Badge>)}</div>
                  </td>
                  <td className="py-2 text-right">{r.leads}</td>
                  <td className="py-2 text-right">{r.contactedPct === null ? '—' : `${r.contactedPct}%`}</td>
                  <td className="py-2 text-right">{r.medianSpeedSeconds === null ? '—' : formatSpeed(r.medianSpeedSeconds)}</td>
                  <td className="py-2 text-right">{r.booked}</td>
                  <td className="py-2 text-right">{r.needsPerson}</td>
                  <td className="py-2 pl-3">{healthBadge(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function KyckConnectOverviewPage() {
  const me = useCurrentUser();
  const isAdmin = me.role === 'admin';
  const query = useQuery({ queryKey: ['kyckconnect', 'overview'], queryFn: fetchOverview, enabled: isAdmin, staleTime: 60_000 });
  const nowMs = Date.now();

  if (!isAdmin) return <KyckConnectHome />;

  const d = query.data;
  return (
    <div className="space-y-6">
      <PageHeader title="Overview" description="What needs Kyck today, how every client is going, and what is not set up. Test leads are left out of every figure." />
      {query.isLoading && <Spinner />}
      {query.error && <p className="text-sm text-red-600">{query.error instanceof ApiError || query.error instanceof Error ? query.error.message : 'Could not load the overview.'}</p>}
      {d && (
        <>
          <section aria-label="Needs Kyck" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              testId="tile-unassigned"
              to={`${KC.relayLog}?status=unassigned`}
              label="Unassigned leads"
              value={d.tiles.unassigned.count}
              note={d.tiles.unassigned.count > 0 ? `Oldest ${ageInWords(d.tiles.unassigned.oldestReceivedAt, nowMs) ?? 'just now'} old. Sent nowhere until assigned.` : 'None waiting.'}
            />
            <Tile
              testId="tile-drafts"
              to={KC.approvals}
              label="Drafts awaiting approval"
              value={d.tiles.drafts.count}
              note={d.tiles.drafts.count > 0 ? `Oldest ${ageInWords(d.tiles.drafts.oldestAt, nowMs) ?? 'just now'} old.` : 'None waiting.'}
            />
            <Tile
              testId="tile-needs-person"
              to={`${KC.leads}?needsPerson=true`}
              label="Leads needing a person"
              value={d.tiles.needsPerson.leads}
              note={d.tiles.needsPerson.leads > 0 ? `Across ${d.tiles.needsPerson.clients} client${d.tiles.needsPerson.clients === 1 ? '' : 's'}.` : 'None waiting.'}
            />
            <Tile
              testId="tile-failed"
              to={`${KC.relayLog}?status=failed`}
              label="Failed CRM deliveries"
              value={d.tiles.failedDeliveries.count}
              note={`In the last ${d.tiles.failedDeliveries.days} days.`}
            />
          </section>

          {d.leadsCapped && (
            <p className="text-xs text-amber-700" data-testid="overview-capped">
              The last 7 days held more leads than the overview reads, so the per-client lead figures are a minimum.
            </p>
          )}

          <ClientTable rows={d.rows} />

          <section className="rounded-md border border-slate-200 bg-white p-4" aria-label="Setup checks">
            <h2 className="mb-3 text-lg font-semibold text-slate-900">Setup checks</h2>
            {d.checks.length === 0 ? (
              <p className="text-sm text-slate-500">Nothing to fix: every client that uses a service passes its setup checks.</p>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm" data-testid="setup-checks">
                {d.checks.map((c) => (
                  <li key={`${c.accountId}-${c.code}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span>
                      <span className="font-medium text-slate-900">{c.accountName}</span>
                      <span className="text-slate-600">: {c.message}</span>
                    </span>
                    <Link className="text-sm font-medium underline" to={fixHref(c.accountId, c.stepId)}>
                      Fix: {SETUP_STEP_LABELS[c.stepId]}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
